'use client';

import { Fragment, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { NavBlockProps, NavLink } from '../types';
import { HEADER_WIDTHS, headerBarSizes, headerRowMaxWidth, resolveHeaderStyle, resolveMobileMenuStyle, splitMenu } from '@realsite/site-config';
import { theme } from '../theme';
import { resolveHeaderColors } from '../logo';

const STANDARD_ROW_MAX = HEADER_WIDTHS.find((w) => w.key === 'standard')!.maxWidth;

/**
 * Pure-ish site header / menu. Logo + links + optional CTA, with a no-JS mobile
 * hamburger (<details>/<summary>).
 *
 * Two modes:
 *  - default (overlayHero false): a solid sticky bar (brand background).
 *  - overlayHero true: the bar overlays a full-bleed hero — transparent (light
 *    text over a subtle scrim) at the very top, turning solid (brand background)
 *    once the visitor scrolls. A negative bottom margin pulls the next block up
 *    underneath the bar so the hero can run edge-to-edge to the top of the page.
 *
 * The scroll-driven flip is the only reason this is a client component; everything
 * else (including the hamburger) is plain markup. The transparent state needs a
 * dark hero behind it to look right — so it's opt-in, and is best previewed on the
 * live site / mobile device frame (the desktop click-to-edit canvas renders blocks
 * in isolation, so it can't show the over-hero overlay).
 */
export function NavBlock({ logoText, logoUrl, logoOverlayUrl, links, ctaLabel, ctaHref, overlayHero, backgroundColor, textColor, headerStyle: headerStyleProp, mobileMenu, headerWidth, headerHeightPx, logoHeightPx }: NavBlockProps) {
  const ref = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!overlayHero) return;
    // Detect "scrolled away from the top" with an IntersectionObserver on a 0-height
    // sentinel at the very top of the page. This is scroll-source-agnostic — it works
    // whether the page scrolls the window (live site) OR an iframe's own viewport
    // (the builder's preview frame) — using the SENTINEL'S OWN window so the root is
    // the right viewport. Falls back to a scroll listener if IO is unavailable.
    const sentinel = sentinelRef.current;
    const win = (sentinel ?? ref.current)?.ownerDocument?.defaultView ?? window;
    if (sentinel && typeof win.IntersectionObserver === 'function') {
      const io = new win.IntersectionObserver(
        ([entry]) => setScrolled(!entry.isIntersecting),
        { root: null, threshold: 0 },
      );
      io.observe(sentinel);
      return () => io.disconnect();
    }
    const onScroll = () => setScrolled(win.scrollY > 8);
    onScroll();
    win.addEventListener('scroll', onScroll, { passive: true });
    return () => win.removeEventListener('scroll', onScroll);
  }, [overlayHero]);

  // Custom header colours (plan E1) — the same rule as the live header (getHeaderScrollTheme): a
  // background makes the bar solid in that colour at all times, over the hero included.
  const custom = resolveHeaderColors({ backgroundColor, textColor });
  const transparent = !!overlayHero && !scrolled && !custom?.background;
  const solid = !transparent;
  // Switch the logo WITH the bar (mirrors the live getHeaderScrollTheme): the reversed logo over the
  // dark hero or a dark custom background, the primary logo on a light bar.
  const reversedLogo = transparent || (!!custom?.background && custom.isDark);
  const displayLogoUrl = reversedLogo ? (logoOverlayUrl || logoUrl) : (logoUrl || logoOverlayUrl);

  const headerStyle = transparent
    ? {
        // Stronger top scrim + a text shadow keep the light links legible over ANY hero —
        // including light/teal/photographic heroes, not just dark ones.
        backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.05))',
        color: '#ffffff',
        textShadow: '0 1px 4px rgba(0,0,0,0.45)',
      }
    : { backgroundColor: custom?.background ?? theme.background, color: custom?.text ?? theme.text };

  // Structure → Header → Width: the live header puts its row in a centred box this wide (with 2rem
  // inside it). This bar has no inner box, so the same box is drawn as side padding. Standard is
  // the live 80rem box too; on a preview narrower than the box the padding is the old px-6.
  const rowMax = headerRowMaxWidth(headerWidth) ?? STANDARD_ROW_MAX;
  const rowWidth = rowMax && rowMax !== 'none'
    ? { paddingLeft: `max(1.5rem, calc((100% - ${rowMax}) / 2 + 2rem))`, paddingRight: `max(1.5rem, calc((100% - ${rowMax}) / 2 + 2rem))` }
    : {};

  // Structure → Header → Height and the logo size: the live header's bar and logo (headerBarSizes),
  // the chosen height grown to fit the logo, and the phone sizes below 768px as the live header
  // does (the md: classes below, evaluated inside the preview frame).
  const barSizes = headerBarSizes(headerHeightPx, logoHeightPx);
  const barVars = {
    '--nav-bar': `${barSizes.desktop.bar}px`,
    '--nav-bar-phone': `${barSizes.phone.bar}px`,
    '--nav-logo': `${barSizes.desktop.logo}px`,
    '--nav-logo-phone': `${barSizes.phone.logo}px`,
  } as CSSProperties;

  const headerClass = [
    'z-30 flex items-center justify-between px-6 transition-colors duration-300',
    // Overlay mode: a FIXED-height bar pinned to the viewport so it reliably stays
    // on screen over the hero (sticky is fragile inside scroll containers / frames).
    // Default mode: the classic in-flow solid sticky bar.
    overlayHero ? 'fixed inset-x-0 top-0' : 'sticky top-0',
    'h-[var(--nav-bar-phone)] md:h-[var(--nav-bar)]',
    solid ? 'border-b border-black/10' : '',
    // backdrop-filter makes the header the containing block for `position: fixed` children, which
    // would shrink a drawer / full-screen phone menu to the bar's own size (same trap as the live
    // header's Login box, #856). Those menus skip the blur; the drop-down keeps it as before.
    solid && resolveMobileMenuStyle(mobileMenu) === 'dropdown' ? 'backdrop-blur' : '',
  ].join(' ');

  const cta =
    ctaLabel && ctaHref ? (
      <a
        href={ctaHref}
        className="blk-btn rounded-md px-4 py-2 text-sm font-medium hover:opacity-90"
      >
        {ctaLabel}
      </a>
    ) : null;

  // Header looks — the SAME resolver the live header uses, so Studio shows what the site shows.
  const style = resolveHeaderStyle(headerStyleProp);
  // The Studio appends the live header's "Login" item as a last link (apps/builder
  // previewNavChrome.ts). Live, Login sits apart from the menu: it is never the Button style's
  // button, it stays on the right of a Centered bar, and it is not counted when Split halves the
  // menu. Hold it back here so the preview lays it out the same way.
  const lastLink = links[links.length - 1];
  const loginLink = lastLink && lastLink.label === 'Login' && lastLink.href === '/admin' ? lastLink : null;
  const menu = loginLink ? links.slice(0, -1) : links;
  const menuStyle = resolveMobileMenuStyle(mobileMenu);
  const minimal = style === 'minimal';
  const overlayMenu = menuStyle !== 'dropdown';
  // Button: the last top-level link without a sub-menu becomes a filled brand-colour button.
  const buttonIndex =
    style === 'button'
      ? menu.map((l, i) => ((l.children?.length ?? 0) === 0 ? i : -1)).filter((i) => i >= 0).pop() ?? -1
      : -1;

  const desktopLink = (l: NavLink, i: number) =>
    l.children && l.children.length > 0 ? (
      <div key={`${i}-${l.label}`} className="group relative">
        <a href={l.href} className="flex items-center gap-1 text-sm font-medium hover:opacity-70" style={{ color: 'inherit' }}>
          {l.label}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </a>
        {/* Always-solid panel for legibility (matches the mobile menu + live header). */}
        {/* NO OPACITY FADE. This is why the dropdown text looked blurry, reported twice.
            An element with 0 < opacity < 1 is composited into its own layer, and a
            composited layer cannot use subpixel (LCD) antialiasing — the browser falls back
            to greyscale AA, which renders text visibly thinner and softer than the sharp
            nav labels beside it. Chromium usually restores subpixel AA once the transition
            settles at exactly 1, but an element that keeps animating opacity can stay
            promoted, and stay soft.
            `visibility` alone creates no layer at all, so the text rasterises exactly like
            the rest of the header. The 150ms fade is not worth illegible menu items. */}
        <div className="invisible absolute left-0 top-full z-40 pt-2 group-hover:visible">
          {/* textShadow: none — the overlay header sets a text shadow on the whole <header>
              for legibility over the hero, and text-shadow INHERITS. Without this reset the
              solid panel renders dark text with the hero's dark shadow (field report,
              twice: "the drop down menu text seems to have a drop shadow or blur"). */}
          <div className="flex min-w-[12rem] flex-col rounded-md border border-black/10 py-1 shadow-lg" style={{ backgroundColor: theme.background, color: theme.text, textShadow: 'none' }}>
            {l.children.map((c) => (
              <a key={c.href} href={c.href} className="px-4 py-2 text-sm font-medium hover:opacity-70" style={{ color: theme.text }}>
                {c.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    ) : i === buttonIndex ? (
      <a key={`${i}-${l.href}`} href={l.href} className="blk-btn rounded-md px-4 py-2 text-sm font-semibold hover:opacity-90" style={{ textShadow: 'none' }}>
        {l.label}
      </a>
    ) : (
      <a key={`${i}-${l.href}`} href={l.href} className="text-sm font-medium hover:opacity-70" style={{ color: 'inherit' }}>
        {l.label}
      </a>
    );

  const logo = (
    <a href="/" className="flex shrink-0 items-center" aria-label="Home">
      {displayLogoUrl ? (
        <img
          src={displayLogoUrl}
          alt={logoText || 'Home'}
          className="h-[var(--nav-logo-phone)] w-auto object-contain md:h-[var(--nav-logo)]"
          // The site's header logo size (style logo_height), clamped like the live header's.
          // Never wider than the frame beside the menu button, like the live header's logo.
          style={{ maxWidth: 'calc(100vw - 6rem)' }}
        />
      ) : (
        <span className="text-xl font-semibold tracking-wide" style={{ fontFamily: theme.headingFont }}>
          {logoText}
        </span>
      )}
    </a>
  );

  const halves = splitMenu(menu.map((l, i) => ({ l, i })));
  const login = loginLink ? desktopLink(loginLink, links.length - 1) : null;
  const big = menuStyle === 'fullscreen';

  const menuLinks = links.map((l, i) => (
    <Fragment key={`${i}-${l.href}`}>
      <a
        href={l.href}
        className={big ? 'rounded px-2 py-2 text-2xl font-semibold hover:opacity-70' : 'rounded px-2 py-1.5 text-sm font-medium hover:opacity-70'}
        style={{ color: theme.text, fontFamily: big ? theme.headingFont : undefined }}
      >
        {l.label}
      </a>
      {l.children?.map((c) => (
        <a
          key={c.href}
          href={c.href}
          className={big ? 'rounded px-2 py-1 text-lg hover:opacity-70' : 'rounded px-2 py-1.5 pl-5 text-sm hover:opacity-70'}
          style={{ color: theme.text }}
        >
          {c.label}
        </a>
      ))}
    </Fragment>
  ));

  // The menu panel. Drop-down: the original small panel under the button. Drawer / Full screen:
  // fixed to the viewport. The button (summary) sits ABOVE the panel and turns into an X, so the
  // no-JS <details> can still be closed with the same control that opened it.
  const panelClass =
    menuStyle === 'drawer'
      ? 'fixed right-0 top-0 z-40 flex h-full w-72 max-w-[85vw] flex-col gap-1 overflow-y-auto border-l border-black/10 p-5 pt-20 shadow-2xl'
      : menuStyle === 'fullscreen'
        ? 'fixed inset-0 z-40 flex flex-col items-center justify-center gap-2 overflow-y-auto p-6 text-center'
        : 'absolute right-0 top-full z-30 mt-2 flex w-56 flex-col gap-1 rounded-md border border-black/10 p-3 shadow-lg';

  const menuButton = (
    <details
      ref={detailsRef}
      onToggle={(e) => setMenuOpen((e.currentTarget as HTMLDetailsElement).open)}
      className={`${minimal ? '' : 'md:hidden '}relative [&_summary::-webkit-details-marker]:hidden`}
    >
      <summary
        className={`relative flex cursor-pointer list-none items-center justify-center gap-2 rounded-md p-2${overlayMenu ? ' z-50' : ''}`}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        // An open drawer / full-screen menu covers the bar, so the X takes the panel's text colour.
        style={{ color: menuOpen && overlayMenu ? theme.text : 'inherit', textShadow: menuOpen && overlayMenu ? 'none' : undefined }}
      >
        {minimal ? <span className="hidden text-sm font-medium md:inline">Menu</span> : null}
        {menuOpen && overlayMenu ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </summary>
      {menuStyle === 'drawer' ? (
        <div
          aria-hidden
          className="fixed inset-0 z-30 bg-black/40"
          onClick={() => {
            if (detailsRef.current) detailsRef.current.open = false;
          }}
        />
      ) : null}
      <div
        className={panelClass}
        data-site-menu={menuStyle}
        // textShadow: none — same reset as the desktop dropdown: the overlay header's
        // hero-legibility text shadow inherits into this solid panel otherwise.
        style={{ backgroundColor: theme.background, color: theme.text, textShadow: 'none' }}
      >
        {menuLinks}
        {cta ? <div className={big ? 'pt-4' : 'pt-1'}>{cta}</div> : null}
      </div>
    </details>
  );

  return (
    <>
      {/* Top-of-page sentinel: the IntersectionObserver flips the bar solid once
          this scrolls out of view. Only needed in overlay mode (0 height, in flow). */}
      {overlayHero ? <div ref={sentinelRef} aria-hidden className="h-px w-full" /> : null}
      <header ref={ref} className={headerClass} style={{ ...headerStyle, ...rowWidth, ...barVars }} data-header-style={style} data-header-width={headerWidth || undefined}>
        {style === 'split' ? (
          <>
            {/* Split: the menu either side of a centred logo on a computer; a phone keeps logo + menu. */}
            <div className="md:hidden">{logo}</div>
            <nav className="hidden flex-1 items-center justify-end gap-6 md:flex">
              {halves.left.map(({ l, i }) => desktopLink(l, i))}
            </nav>
            <div className="hidden shrink-0 px-8 md:flex">{logo}</div>
            <nav className="hidden flex-1 items-center justify-start gap-6 md:flex">
              {halves.right.map(({ l, i }) => desktopLink(l, i))}
              {login}
              {cta}
            </nav>
            {menuButton}
          </>
        ) : (
          <>
            {logo}
            {/* Desktop: inline links — inherit the header color so they flip on scroll. A link
                with children renders as a hover dropdown (one level deep) — mirrors the live header.
                Centered puts them in the middle of the bar; Minimal hides them behind Menu. */}
            {!minimal ? (
              <nav className={`hidden items-center gap-6 md:flex${style === 'centered' ? ' flex-1 justify-center' : ''}`}>
                {menu.map(desktopLink)}
                {style === 'centered' ? null : login}
              </nav>
            ) : null}
            {style === 'centered' && login ? <nav className="hidden items-center md:flex">{login}</nav> : null}
            {/* Desktop: CTA (keeps the original three-up layout) */}
            {!minimal ? <div className="hidden md:block">{cta ?? <span aria-hidden />}</div> : null}
            {menuButton}
          </>
        )}
      </header>
    </>
  );
}
